import type { CountryCode } from 'libphonenumber-js'
import { useSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useScrollToAnchorOnceLoaded } from 'src/modules/core/hooks/use-scroll-to-anchor-once-loaded'
import { buildCountryOptions } from 'src/modules/core/utils/country.utils'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import { useUpdateProfile } from 'src/modules/user/hooks/use-update-profile'
import { COUNTRY_ANCHOR_ID, findCountryOption, resolveLanguageSwitch } from './account-country-card.domain'

function useSaveCountry(onSaved: () => void) {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const updateProfile = useUpdateProfile()

  const save = async (country: CountryCode) => {
    try {
      await updateProfile.mutateAsync({ default_country_code: country })
      onSaved()
      enqueueSnackbar(formatMessage({ id: 'settings.saved' }), { variant: 'success' })
    } catch {
      // The cache still holds the saved value, so the country stays staged to retry.
      enqueueSnackbar(formatMessage({ id: 'settings.country.saveFailed' }), { variant: 'error' })
    }
  }

  return { save, isSaving: updateProfile.isLoading }
}

export function useAccountCountryCard() {
  const { data: profile } = useProfile()
  const options = useMemo(buildCountryOptions, [])
  const [staged, setStaged] = useState<CountryCode | undefined>(undefined)
  const { save, isSaving } = useSaveCountry(() => setStaged(undefined))
  useScrollToAnchorOnceLoaded(COUNTRY_ANCHOR_ID, !!profile)

  const saved = findCountryOption(options, profile?.default_country_code)
  const isStaged = staged !== undefined && staged !== saved?.value
  const selected = isStaged ? findCountryOption(options, staged) : saved
  const languageSwitch = isStaged
    ? resolveLanguageSwitch({
        messageLanguage: profile?.message_language,
        savedCountry: profile?.default_country_code,
        stagedCountry: staged,
      })
    : null

  return {
    profile,
    options,
    saved,
    selected,
    isStaged,
    languageSwitch,
    isSaving,
    stage: setStaged,
    cancel: () => setStaged(undefined),
    save: () => (isStaged ? save(staged) : Promise.resolve()),
  }
}
